/**
 * Portfolio Dashboard Container
 * Smart container using React Query via usePortfolioLevelData hook.
 */

import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePortfolioStore } from '../store/portfolioStore';
import { usePortfolioLevelData } from '../../../hooks/usePortfolioLevelData';
import { httpClient } from '../../../lib/httpClient';
import PortfolioDashboardPresenter from '../components/PortfolioDashboardPresenter';

/**
 * Container component for Portfolio Dashboard
 * Supports two route patterns:
 *   /portfolio                                         → my-book view (always)
 *   /portfolio/client/:clientId/portfolio/:portfolioId  → portfolio drill-down
 */
const PortfolioDashboardContainer: React.FC = () => {
  // URL params from route (TASK-371: drill-down routing)
  const { clientId: urlClientId, portfolioId: urlPortfolioId } = useParams<{
    clientId?: string;
    portfolioId?: string;
  }>();

  const {
    selectedPortfolio,
    timeRange,
    setSelectedPortfolio,
    setTimeRange,
  } = usePortfolioStore();

  // URL-based tab state
  const VALID_TABS = ['insights', 'composition', 'performance', 'risk', 'personalization', 'history', 'clients'] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.includes(searchParams.get('tab') as typeof VALID_TABS[number])
    ? searchParams.get('tab')!
    : 'composition';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'composition') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Benchmark selector state (default SPI)
  const [benchmark, setBenchmark] = useState<string>('SPI');

  // Determine view level: URL params take priority over Zustand viewContext
  const viewContext = usePortfolioStore(state => state.viewContext);
  const setViewContext = usePortfolioStore(state => state.setViewContext);

  // Bare /portfolio is the section entry point (top-nav "Portfolio Analytic") and always
  // means My Book. It used to redirect to `/portfolio/client/${viewContext.clientId}` from
  // the store instead — a `replace: true` bounce, so Back could not escape it, onto an id
  // nothing had revalidated. The nav item now goes where its label says.
  useEffect(() => {
    if (urlClientId || urlPortfolioId) return; // URL-driven — don't interfere
    if (viewContext.level !== 'my-book') {
      setViewContext({ level: 'my-book', clientId: undefined, portfolioId: undefined });
    }
  }, []); // Run once on mount only

  const level = urlClientId && urlPortfolioId
    ? 'portfolio' as const
    : urlClientId
      ? 'client' as const
      : 'my-book' as const;

  // `selectedPortfolio` used to be the last fallback here. It is a portfolio identifier
  // (default 'all'), never a client id — and `level !== 'my-book'` only holds when
  // `urlClientId` is set, so the branch was unreachable as well as wrong.
  const effectiveClientId = urlClientId || viewContext.clientId;
  const effectivePortfolioId = urlPortfolioId || viewContext.portfolioId;

  // React Query data fetching via unified hook
  const { clientData, globalData, loading, refetch } = usePortfolioLevelData({
    level,
    clientId: effectiveClientId,
    portfolioId: effectivePortfolioId,
    period: timeRange,
    benchmark,
  });

  // Clients list via React Query
  const { data: clientsData } = useQuery({
    queryKey: ['dashboard-clients-list'],
    queryFn: async () => {
      const data = await httpClient.get<any>('/api/clients');
      return (data.clients || []) as Array<{ id: string; name: string }>;
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  const clients = clientsData ?? [];

  // Handle portfolio change
  const handlePortfolioChange = (portfolio: string) => {
    setSelectedPortfolio(portfolio);

    if (portfolio === 'all') {
      if (activeTab === 'history') {
        setActiveTab('clients');
      }
    } else {
      if (activeTab === 'clients') {
        setActiveTab('composition');
      }
    }
  };

  // Handle client selection from Clients tab
  const handleClientSelect = (clientId: string) => {
    setSelectedPortfolio(clientId);
    setActiveTab('composition');
  };

  return (
    <PortfolioDashboardPresenter
      selectedPortfolio={selectedPortfolio}
      timeRange={timeRange}
      activeTab={activeTab}
      loading={loading}
      clientData={clientData}
      globalData={globalData}
      clients={clients}
      onPortfolioChange={handlePortfolioChange}
      onTimeRangeChange={setTimeRange}
      onTabChange={setActiveTab}
      onClientSelect={handleClientSelect}
      onRefresh={refetch}
      benchmark={benchmark}
      onBenchmarkChange={setBenchmark}
    />
  );
};

/**
 * Enhanced container with error boundary
 */
export const PortfolioDashboardWithErrorBoundary: React.FC = () => {
  return (
    <PortfolioErrorBoundary>
      <PortfolioDashboardContainer />
    </PortfolioErrorBoundary>
  );
};

class PortfolioErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Portfolio Dashboard Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Portfolio Dashboard Error
              </h2>
              <p className="text-gray-600 mb-4">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Reload Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PortfolioDashboardContainer;
